import React, { useState, useEffect } from 'react';
import { LabValue } from '../types';
import { roundValue } from '../utils/unitConverter';

interface CorrectionDialogProps {
  marker: string;
  labValue: LabValue;
  onCorrect: (marker: string, correctedValue: number, correctedUnit?: string) => void;
  onCancel: () => void;
  mode?: 'value' | 'unit' | 'both';
  unitMismatchInfo?: {
    detectedUnit: string;
    reason: string;
    suggestedValue: number;
  };
}

export const CorrectionDialog: React.FC<CorrectionDialogProps> = ({
  marker,
  labValue,
  onCorrect,
  onCancel,
  mode = 'value',
  unitMismatchInfo
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<number | null>(null);
  const [showManualInput, setShowManualInput] = useState<boolean>(false);
  const [correctedUnit, setCorrectedUnit] = useState<string>(labValue.units || '');
  const [showUnitOptions, setShowUnitOptions] = useState<boolean>(false);
  
  // Extract suggested value from warning if available
  useEffect(() => {
    if (unitMismatchInfo) {
      const roundedSuggestion = roundValue(unitMismatchInfo.suggestedValue, marker);
      setSelectedSuggestion(roundedSuggestion);
      setInputValue(roundedSuggestion.toString());
      setCorrectedUnit(unitMismatchInfo.detectedUnit);
    } else if (labValue.validation_warnings && labValue.validation_warnings.length > 0) {
      const suggestion = labValue.validation_warnings[0].suggestion;
      if (suggestion) {
        // Extract numeric value from suggestion text
        const match = suggestion.match(/(?:value:|Consider)\s*([\d.]+)/);
        if (match) {
          const parsedValue = parseFloat(match[1]);
          const roundedValue = roundValue(parsedValue, marker);
          setSelectedSuggestion(roundedValue);
          setInputValue(roundedValue.toString());
        }
      }
    }
  }, [labValue, unitMismatchInfo, marker]);

  const handleSubmit = () => {
    const value = parseFloat(inputValue);
    if (!isNaN(value)) {
      const roundedValue = roundValue(value, marker);
      if (mode === 'unit' || mode === 'both') {
        onCorrect(marker, roundedValue, correctedUnit);
      } else {
        onCorrect(marker, roundedValue);
      }
    }
  };

  const handleUseSuggested = () => {
    if (selectedSuggestion !== null) {
      const roundedValue = roundValue(selectedSuggestion, marker);
      if (unitMismatchInfo) {
        onCorrect(marker, roundedValue, unitMismatchInfo.detectedUnit);
      } else {
        onCorrect(marker, roundedValue);
      }
    }
  };


  // Helper function to get friendly marker names
  const getFriendlyMarkerName = (marker: string) => {
    const markerMap: { [key: string]: string } = {
      'HCT': 'HCT (Hematocrit)',
      'HAEMOGLOBIN': 'Hemoglobin',
      'HEMOGLOBIN': 'Hemoglobin',
      'HGB': 'Hemoglobin',
      'HB': 'Hemoglobin',
      'WCC': 'White Cell Count',
      'RBC': 'Red Blood Cell Count',
      'WBC': 'White Blood Cell Count',
      'ALT': 'ALT (Liver Enzyme)',
      'AST': 'AST (Liver Enzyme)',
      'MCHC': 'MCHC (Mean Corpuscular Hemoglobin Concentration)',
    };
    return markerMap[marker.toUpperCase()] || marker;
  };
  
  // Get unit options for a test
  const getUnitOptions = (marker: string): string[] => {
    const markerUpper = marker.toUpperCase();
    if (markerUpper === 'HEMOGLOBIN' || markerUpper === 'HGB' || markerUpper === 'HB') {
      return ['g/dL', 'g/L'];
    } else if (markerUpper === 'MCHC') {
      return ['g/dL', 'g/L'];
    } else if (markerUpper === 'CHOLESTEROL' || markerUpper.includes('LDL') || markerUpper.includes('HDL')) {
      return ['mg/dL', 'mmol/L'];
    } else if (markerUpper === 'GLUCOSE') {
      return ['mg/dL', 'mmol/L'];
    }
    return [];
  };

  // Helper to format the visual comparison
  const getVisualComparison = () => {
    if (!selectedSuggestion) return null;
    
    const originalPercent = labValue.value * 100;
    const suggestedPercent = selectedSuggestion * 100;
    
    // Check if this is likely a percentage issue
    if (marker.toUpperCase() === 'HCT' && originalPercent > 100) {
      return {
        showPercentage: true,
        originalPercent: originalPercent.toFixed(1),
        suggestedPercent: suggestedPercent.toFixed(2)
      };
    }
    return null;
  };

  const visualComparison = getVisualComparison();

  return (
    <div className="correction-dialog-overlay" onClick={onCancel}>
      <div className="correction-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>
          {unitMismatchInfo ? 'Fix Unit Mislabeling' : 
           labValue.validation_warnings?.length ? 'Fix' : 'Edit'} {getFriendlyMarkerName(marker)} 
          {mode === 'unit' ? 'Unit' : mode === 'both' ? 'Value & Unit' : 'Value'}
        </h3>
        
        {unitMismatchInfo ? (
          <div className="unit-mismatch-explanation">
            <div className="mismatch-alert">
              <span className="alert-icon">⚠️</span>
              <div>
                <p className="alert-title">Unit Mislabeling Detected</p>
                <p className="alert-message">{unitMismatchInfo.reason}</p>
              </div>
            </div>
            
            <div className="unit-comparison">
              <div className="comparison-item error">
                <span className="comparison-icon">❌</span>
                <div>
                  <div className="comparison-label">Lab reported:</div>
                  <div className="comparison-value">{labValue.value} {labValue.units}</div>
                  <div className="comparison-note">(This appears incorrect)</div>
                </div>
              </div>
              
              <div className="comparison-item success">
                <span className="comparison-icon">✓</span>
                <div>
                  <div className="comparison-label">Should be:</div>
                  <div className="comparison-value">{unitMismatchInfo.suggestedValue} {unitMismatchInfo.detectedUnit}</div>
                  <div className="comparison-note">(Corrected unit)</div>
                </div>
              </div>
            </div>
          </div>
        ) : labValue.validation_warnings?.length ? (
          <div className="error-explanation">
            <div className="value-comparison-visual">
              <div className="comparison-item error">
                <span className="comparison-icon">❌</span>
                <div>
                  <div className="comparison-label">What we found:</div>
                  <div className="comparison-value">{labValue.value}</div>
                  {visualComparison?.showPercentage && (
                    <div className="comparison-note">({visualComparison.originalPercent}% - way too high!)</div>
                  )}
                </div>
              </div>
              
              {selectedSuggestion && (
                <div className="comparison-item success">
                  <span className="comparison-icon">✓</span>
                  <div>
                    <div className="comparison-label">We think it might be:</div>
                    <div className="comparison-value">{selectedSuggestion}</div>
                    {visualComparison?.showPercentage && (
                      <div className="comparison-note">({visualComparison.suggestedPercent}% - normal range)</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="error-description">
              <p>
                {labValue.validation_warnings?.[0]?.type === 'extreme_value' 
                  ? "This looks like a decimal point error. The value appears to be missing a leading zero."
                  : labValue.validation_warnings?.[0]?.message || "This value appears to be incorrect."}
              </p>
              {labValue.reference_range && (
                <p className="range-info">
                  Expected range: <strong>{labValue.reference_range}</strong>
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="edit-explanation">
            <div className="current-value-display">
              <label>Current Value:</label>
              <div className="value-display">
                {labValue.value} {labValue.units}
              </div>
            </div>
            {labValue.reference_range && (
              <div className="reference-display">
                <label>Reference Range:</label>
                <div className="value-display">{labValue.reference_range}</div>
              </div>
            )}
          </div>
        )}

        <div className="action-section">
          {unitMismatchInfo ? (
            <>
              <button 
                className="btn btn-fix-primary"
                onClick={handleUseSuggested}
              >
                <span className="btn-icon">✓</span>
                Fix to {unitMismatchInfo.suggestedValue} {unitMismatchInfo.detectedUnit}
              </button>
              
              <div className="alternative-action">
                <button 
                  className="btn-link"
                  onClick={() => setShowUnitOptions(!showUnitOptions)}
                >
                  Choose different unit
                </button>
              </div>
              
              {showUnitOptions && (
                <div className="unit-options-section">
                  <label>Select correct unit:</label>
                  <div className="unit-options">
                    {getUnitOptions(marker).map(unit => (
                      <button
                        key={unit}
                        className={`unit-option ${correctedUnit === unit ? 'selected' : ''}`}
                        onClick={() => {
                          setCorrectedUnit(unit);
                          // Recalculate value if needed
                          if (unit !== labValue.units) {
                            // You might want to add conversion logic here
                          }
                        }}
                      >
                        {unit}
                      </button>
                    ))}
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={handleSubmit}
                  >
                    Apply Unit Change
                  </button>
                </div>
              )}
            </>
          ) : selectedSuggestion !== null ? (
            <>
              <button 
                className="btn btn-fix-primary"
                onClick={handleUseSuggested}
              >
                <span className="btn-icon">✓</span>
                Fix to {selectedSuggestion}
              </button>
              
              <div className="alternative-action">
                <button 
                  className="btn-link"
                  onClick={() => setShowManualInput(!showManualInput)}
                >
                  Enter a different value
                </button>
              </div>
              
              {showManualInput && (
                <div className="manual-input-section">
                  <div className="input-group">
                    <input
                      id="corrected-value"
                      type="number"
                      step="any"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Enter correct value"
                      autoFocus
                    />
                    <span className="units-label">{labValue.units}</span>
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={!inputValue || isNaN(parseFloat(inputValue))}
                  >
                    Save
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="manual-input-section">
              <label>Enter the correct value:</label>
              <div className="input-group">
                <input
                  id="corrected-value"
                  type="number"
                  step="any"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter correct value"
                  autoFocus
                />
                <span className="units-label">{labValue.units}</span>
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!inputValue || isNaN(parseFloat(inputValue))}
              >
                Save Correction
              </button>
            </div>
          )}
        </div>

        <div className="dialog-footer">
          <p className="verify-reminder">
            <span className="icon">👁️</span> Please check your PDF to confirm the correct value - it may be different from our suggestion
          </p>
          <button className="btn-link cancel-link" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};